import AppCore from "@/application/AppCore.vue";
import { createRouter, createWebHistory } from "vue-router";
import { createPinia } from "pinia";
import { routes } from "@/application/router/routes"
import { authenticateGuard } from "@/application/router/guards/authenticateGuard";
import '../css/app.scss';
import '@/application/utils/element-plus-styles.ts';
import passwordHealth from "@/application/plugins/passwordHealth";
import '@/application/resources/Icons';
import { SvgVue } from "@/application/plugins/svgVue";
import { vaporAssetPlugin } from "@/application/plugins/vapor";
import { setRouter } from "@/api/lib/routerRef";
import { checkupBeforeGuard } from "@/application/router/guards/checkupBeforeGuard";
import { checkupAfterGuard } from "@/application/router/guards/checkupAfterGuard";
import { scrollBehavior } from '@/application/router/utils/scrollBehavior';
import { cleanUpServiceWorker } from "@/application/utils/worker/cleanUpServiceWorker";
import { registerServiceWorker } from "@/application/utils/worker/registerServiceWorker";
import { createApp } from 'vue';
import { captureException } from "@sentry/vue";
import { initSentry } from "@/application/plugins/sentry";
import type { URLOpenListenerEvent } from '@capacitor/app';
import { App } from '@capacitor/app';
import { isLocalEnv, isWebPlatform } from "@/application/utils/envInfo";
import { updateDocumentHead } from "@/application/router/guards/updateDocumentHead";
import { configureSecureStorage } from "@/application/plugins/secretStore";
import { performAppUpdate } from "@/application/plugins/appUpdate";
import { useUserStore } from "@/user/store/userStore";
import { addPushNotificationListeners } from "@/application/plugins/pushNotification";
import { SplashScreen } from "@capacitor/splash-screen";
import { Finanzmanager } from "@/application/router/types/types";
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
import { IonicVue } from '@ionic/vue';

async function initApp() {

  await Promise.all([
    performAppUpdate(),
    configureSecureStorage(),
  ]);

  const pinia = createPinia();
  const router = createRouter({ scrollBehavior, history: createWebHistory(), routes });
  router.beforeEach(authenticateGuard);
  router.beforeEach(checkupBeforeGuard);
  router.afterEach(checkupAfterGuard);
  router.afterEach(updateDocumentHead);
  // Uncaught errors thrown inside navigation guards reject the router.push() promise. Callers that
  // don't await push() (e.g. the login flow) would otherwise leak these as unhandled rejections with
  // no useful stack. Capturing here gives them a real origin instead of a synthetic page-path frame.
  router.onError((error) => captureException(error));
  setRouter(router);

  const app = createApp(AppCore);
  initSentry(app, router);

  app.use(SvgVue)
  app.use(passwordHealth);
  app.use(router);
  app.use(pinia);
  app.use(vaporAssetPlugin);
  app.use(IonicVue);

  const { checkLockscreenSettings, lockApp, updateBiometry, setPausedAt } = useUserStore();

  const promises = Promise.all([
    checkLockscreenSettings(),
    updateBiometry(),
  ]);

  App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
    await lockApp();

    const parsedUrl = new URL(event.url);
    const query = Object.fromEntries(parsedUrl.searchParams.entries());

    if (parsedUrl.pathname) router
      .push({ path: parsedUrl.pathname, query })
      .then((e) => {
        if (e instanceof Error) router.push({ path: parsedUrl.pathname, query })
      });
  }).catch(captureException);

  App.addListener('resume', async () => {
    // when closing the app on the logout page, route to login on next app usage
    if (router.currentRoute.value.name === Finanzmanager.LOGOUT) {
      router.push({ name: Finanzmanager.LOGIN }).catch(captureException)
    }

    await lockApp();

    await Promise.all([
      updateBiometry(),
      performAppUpdate(),
      useUserStore().isAuthenticated && useBaseDataStore().fetchBaseData(),
    ]);
  }).catch(captureException);

  App.addListener('pause', () => {
    setPausedAt();
  }).catch(captureException);

  app.mount('#app');

  addPushNotificationListeners().catch(captureException);
  await promises;
  await lockApp();
  SplashScreen.hide().catch(captureException);

  cleanUpServiceWorker().then(() => {
    if (isWebPlatform && !isLocalEnv) registerServiceWorker();
  }).catch(captureException);
}

initApp().catch(captureException);
